import { useQuery } from '@tanstack/react-query';
import ReactECharts from 'echarts-for-react';
import { Boxes, Clock, Scale } from 'lucide-react';
import Card from '../../components/ui/Card';
import KpiTile from '../../components/ui/KpiTile';
import MachineFilters from '../../components/ui/MachineFilters';
import PageHeader from '../../components/ui/PageHeader';
import { useMachineFilters } from '../../hooks/useMachineFilters';
import { insightsApi, type LifterInsightRow } from '../../services/insightsApi';

const formatKg = (n: number) => `${n.toLocaleString(undefined, { maximumFractionDigits: 0 })} kg`;
const formatNum = (n: number) => n.toLocaleString();
const formatMinutes = (seconds: number) => `${Math.round(seconds / 60).toLocaleString()} min`;

const LifterDashboardView = () => {
  const f = useMachineFilters({ machineType: 'lifter' });

  const insightsQuery = useQuery({
    queryKey: ['insights', 'lifter', f.selectedMachineId, f.from, f.to],
    enabled: Boolean(f.selectedMachineId),
    queryFn: async () =>
      (await insightsApi.lifter(f.selectedMachineId as string, f.from, f.to)).data,
  });

  const rows = insightsQuery.data?.rows ?? [];
  const totals = insightsQuery.data?.totals;

  return (
    <>
      <PageHeader
        title="Lifter Dashboard"
        subtitle="Daily lifts and throughput per machine."
        actions={
          <MachineFilters
            machines={f.machines}
            selectedMachineId={f.selectedMachineId}
            onMachineChange={f.setMachineId}
            from={f.from}
            to={f.to}
            onFromChange={f.setFrom}
            onToChange={f.setTo}
            loading={f.machinesLoading}
          />
        }
      />

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <KpiTile
          label="Total lifts"
          value={totals ? formatNum(totals.totesLoaded) : '—'}
          Icon={Boxes}
          loading={insightsQuery.isFetching}
        />
        <KpiTile
          label="Kgs loaded"
          value={totals ? formatKg(totals.kgsLoaded) : '—'}
          Icon={Scale}
          loading={insightsQuery.isFetching}
        />
        <KpiTile
          label="Total loading time"
          value={totals ? formatMinutes(totals.timeLoading) : '—'}
          Icon={Clock}
          loading={insightsQuery.isFetching}
        />
      </section>

      <section className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Card title="Daily lifts">
          <ReactECharts option={liftsOption(rows)} style={{ height: 280 }} notMerge lazyUpdate />
        </Card>
        <Card title="Daily kgs lifted">
          <ReactECharts option={kgsOption(rows)} style={{ height: 280 }} notMerge lazyUpdate />
        </Card>
      </section>

      <Card className="mt-6" title="Per-day breakdown">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="py-2 pr-4 font-medium">Date</th>
                <th className="py-2 pr-4 font-medium">Lifts</th>
                <th className="py-2 pr-4 font-medium">Kgs</th>
                <th className="py-2 pr-4 font-medium">Loading time (min)</th>
                <th className="py-2 pr-4 font-medium">Avg per lift (kg)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {rows.map((r) => (
                <tr key={r.ts} className="hover:bg-slate-50">
                  <td className="py-2 pr-4 font-mono text-xs text-slate-500">{r.ts}</td>
                  <td className="py-2 pr-4">{r.totesLoaded}</td>
                  <td className="py-2 pr-4">{r.kgsLoaded.toFixed(0)}</td>
                  <td className="py-2 pr-4">{(r.timeLoading / 60).toFixed(0)}</td>
                  <td className="py-2 pr-4">
                    {r.totesLoaded > 0 ? (r.kgsLoaded / r.totesLoaded).toFixed(1) : '—'}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-xs text-slate-400">
                    No data in window.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
};

const liftsOption = (rows: LifterInsightRow[]) => ({
  grid: { left: 50, right: 20, top: 20, bottom: 30 },
  tooltip: { trigger: 'axis' as const },
  xAxis: {
    type: 'category' as const,
    data: rows.map((r) => r.ts.slice(5)),
    axisLabel: { color: '#64748b' },
  },
  yAxis: {
    type: 'value' as const,
    axisLabel: { color: '#64748b' },
    splitLine: { lineStyle: { color: '#e2e8f0' } },
  },
  series: [
    {
      type: 'bar' as const,
      data: rows.map((r) => r.totesLoaded),
      itemStyle: { color: '#0ea5e9', borderRadius: [4, 4, 0, 0] },
    },
  ],
});

const kgsOption = (rows: LifterInsightRow[]) => ({
  grid: { left: 50, right: 20, top: 20, bottom: 30 },
  tooltip: { trigger: 'axis' as const },
  xAxis: {
    type: 'category' as const,
    data: rows.map((r) => r.ts.slice(5)),
    axisLabel: { color: '#64748b' },
  },
  yAxis: {
    type: 'value' as const,
    axisLabel: { color: '#64748b', formatter: '{value} kg' },
    splitLine: { lineStyle: { color: '#e2e8f0' } },
  },
  series: [
    {
      type: 'line' as const,
      smooth: true,
      showSymbol: false,
      data: rows.map((r) => r.kgsLoaded),
      itemStyle: { color: '#8b5cf6' },
      areaStyle: { color: 'rgba(139, 92, 246, 0.15)' },
    },
  ],
});

export default LifterDashboardView;
